import type { SanityDocument } from '@sanity/client';
import type { ImageWithMetadata} from '../components/SanityImage';
import { imageWithMetadataGroq } from '../components/SanityImage';
import GridItemImage from './GridItemImage';
import type { Tag } from './GridItemTags';
import GridItemTags from './GridItemTags';
import * as style from './TaggedGridItem.module.css';

export type TaggedSanityDocument<T extends string = string> = SanityDocument & { tags?: Tag[]; _type: T };

export const taggedGridItemGroq = /* groq */ `{
    _id,
    _type,
    _createdAt,
    _updatedAt,
    firstPublishedAt,
    title,
    ingress,
    'category': select(
        defined(parent -> parent) => parent -> title,
    ),
    parent-> {
        _id,
        slug
    },
    bannerImage ${imageWithMetadataGroq},
    tags[] {
        _key,
        ...(@ -> {
            _id,
            name,
            slug,
            mainTagCategory -> {
                _id,
                name,
                slug
            }
        })
    }
}`;

export type TaggedGridItemDocument<T extends string = string> = TaggedSanityDocument<T>;

export interface TaggedGridItemProps extends TaggedGridItemDocument {
    path?: string;
    category?: string;
    parent?: {
        _id: string;
        slug: {
            current: string;
        };
    };
    title?: string;
    ingress?: string;
    bannerImage?: ImageWithMetadata | string;
    showIngress?: boolean;
    showCategory?: boolean;
    hideImage?: boolean;
    hideTags?: boolean;
    desktopImageWidth?: number;
}

export default function TaggedGridItem({
    bannerImage,
    path,
    category,
    title,
    ingress,
    tags,
    showCategory = false,
    showIngress = false,
    hideImage = false,
    hideTags = false,
    desktopImageWidth,
}: TaggedGridItemProps) {
    return (
        <a href={path} className={style.item}>
            <GridItemImage image={bannerImage} hideImage={hideImage} desktopWidth={desktopImageWidth} />
            <div className={style.info}>
                {showCategory && <span className={style.category}>{category}</span>}
                <h2>{title}</h2>
                {showIngress && ingress?.split('\n').map((t, i) => <p key={i}>{t}</p>)}
                {!hideTags && <GridItemTags tags={tags} />}
            </div>
        </a>
    );
}
